import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Send, Heart } from "lucide-react";
import { api, LOGO_URL } from "../lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're in. Welcome to the Forge.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email");
    } finally { setLoading(false); }
  };

  return (
    <footer className="border-t border-white/10 bg-[#0A0A0A] pb-16 md:pb-0" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <img src={LOGO_URL} alt="MileForge" className="h-24 w-auto object-contain -ml-3" />
          <p className="mt-2 text-sm text-white/60 font-body max-w-xs">
            Forged through miles. Premium adventure lifestyle products for riders, travellers and explorers.
          </p>
          <p className="mt-3 text-xs font-body text-white/40">
            A <a href="https://rides.touringcraft.com" target="_blank" rel="noreferrer" className="text-[#D96A1B] hover:underline">Touring Craft</a> brand.
          </p>
          <div className="flex flex-col gap-2 mt-6">
            <a
              href="https://instagram.com/mileforge"
              target="_blank"
              rel="noreferrer"
              data-testid="social-instagram-mileforge"
              className="text-white/70 hover:text-[#D96A1B] transition-colors flex items-center gap-2"
            >
              <Instagram size={18} />
              <span className="text-xs font-display tracking-widest uppercase">@mileforge</span>
            </a>
            <a
              href="https://instagram.com/touringcraft"
              target="_blank"
              rel="noreferrer"
              data-testid="social-instagram-touringcraft"
              className="text-white/55 hover:text-[#D96A1B] transition-colors flex items-center gap-2"
            >
              <Instagram size={16} />
              <span className="text-[10px] font-display tracking-widest uppercase">@touringcraft <span className="text-white/30">— community</span></span>
            </a>
          </div>
          <div className="mt-5 space-y-1 text-xs font-body text-white/50">
            <a href="mailto:hello@mileforge.in" className="block hover:text-white">hello@mileforge.in</a>
            <a href="tel:+919884041979" className="block hover:text-white">+91 98840 41979</a>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="overline mb-4">Shop</div>
          <ul className="space-y-2 text-sm font-body text-white/70">
            <li><Link to="/shop?category=riding-gear" className="hover:text-white">Riding Gear</Link></li>
            <li><Link to="/shop?category=moto-essentials" className="hover:text-white">Moto Essentials</Link></li>
            <li><Link to="/shop?category=maintenance" className="hover:text-white">Maintenance</Link></li>
            <li><Link to="/shop?category=lifestyle" className="hover:text-white">Lifestyle</Link></li>
            <li><Link to="/shop?category=adventure" className="hover:text-white">Adventure</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="overline mb-4">Brand</div>
          <ul className="space-y-2 text-sm font-body text-white/70">
            <li><Link to="/about" className="hover:text-white">Our Story</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
            <li><Link to="/track-order" data-testid="footer-track-order" className="hover:text-white">Track My Order</Link></li>
            <li><a href="https://rides.touringcraft.com" target="_blank" rel="noreferrer" className="hover:text-white">Journal</a></li>
            <li><a href="https://instagram.com/touringcraft" target="_blank" rel="noreferrer" className="hover:text-white">Community</a></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="overline mb-4">Legal</div>
          <ul className="space-y-2 text-sm font-body text-white/70" data-testid="footer-legal-links">
            <li><Link to="/terms" data-testid="footer-terms" className="hover:text-white">Terms & Conditions</Link></li>
            <li><Link to="/privacy-policy" data-testid="footer-privacy" className="hover:text-white">Privacy Policy</Link></li>
            <li><Link to="/refund-policy" data-testid="footer-refund" className="hover:text-white">Refunds & Cancellations</Link></li>
            <li><Link to="/shipping-policy" data-testid="footer-shipping" className="hover:text-white">Shipping Policy</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="overline mb-4">Join the Forge</div>
          <p className="text-sm font-body text-white/70 mb-3">
            Ride updates, drops and exclusive offers.
          </p>
          <form onSubmit={subscribe} className="flex">
            <input
              data-testid="footer-newsletter-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              required
              placeholder="rider@email.com"
              className="flex-1 bg-[#111] border border-white/15 px-4 py-3 text-sm font-body focus:outline-none focus:border-[#D96A1B] text-white"
            />
            <button
              data-testid="footer-newsletter-submit"
              disabled={loading}
              className="bg-[#D96A1B] hover:bg-[#E87A2A] text-white px-5 transition-colors flex items-center"
            >
              <Send size={16} />
            </button>
          </form>
          <p className="mt-4 text-[10px] text-white/40 font-body leading-relaxed">
            Payments securely processed by <span className="text-white/70">Cashfree Payments India Pvt. Ltd.</span> UPI · Cards · Netbanking · COD accepted.
          </p>
        </div>
      </div>

      <div className="border-t border-white/5 py-5 flex flex-col sm:flex-row gap-2 sm:gap-4 items-center justify-between max-w-7xl mx-auto px-6 lg:px-10 text-xs font-body text-white/40">
        <div data-testid="made-in-india" className="flex items-center gap-1.5 tracking-wide">
          <span>Proudly made with</span>
          <Heart size={13} className="text-red-500 fill-red-500" />
          <span>for adventure from India</span>
        </div>
        <div>© {new Date().getFullYear()} MileForge · Forged Through Miles.</div>
      </div>

      <div data-testid="legal-entity-block" className="border-t border-white/5 py-4 max-w-7xl mx-auto px-6 lg:px-10 text-[11px] font-body text-white/45 leading-relaxed">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-1 justify-center sm:justify-between text-center sm:text-left">
          <span><span className="text-white/60">Legal Entity:</span> Touring Craft &amp; Riders Cafe</span>
          <span><span className="text-white/60">GSTIN:</span> 33AIUPC4644A1ZL</span>
          <span><span className="text-white/60">Business Type:</span> Proprietorship</span>
          <span><span className="text-white/60">Registered Office:</span> Chennai, Tamil Nadu, India</span>
        </div>
      </div>
    </footer>
  );
}
